import {
  BioSampleContainer,
  BioSampleContainerQuery,
  BioSampleContainerRef,
  BioSampleKitType,
  KitTypeAndDescription,
  KitTypeAndDescriptions,
} from '@csp/csp-common-hbs-model';
import { ApiOptions, Maybe, PagedResult } from '@csp/csp-common-model';
import { BioSamplingContainerRestServiceV1 } from '@csp/dmdp-api-client';
import { toRestOptions } from '@csp/csp-fe-auth';
import { PagedResultMapper } from '@csp/csp-common-mapper';
import { CacheKey, MemCacheService } from '@csp/csp-common-memcache';
import { uniq } from 'lodash';
import { BioSamplingSupplyKitInfoQueryInV1 } from '@csp/dmdp-api-hbs-supply-dto';
import { KitTypeAndDescriptionsMapper } from '../mapper/KitTypeAndDescriptionsMapper';
import { BioSampleContainerQueryMapper } from '../mapper/BioSampleContainerQueryMapper';
import { BioSampleContainerMapper } from '../mapper/BioSampleContainerMapper';

const CACHE_TTL_SECS = 3_600;

const toContainerCacheKey = (ref: BioSampleContainerRef): string =>
  CacheKey.from(['cf', ref.kitCode, ref.containerNumber]);
const toDescriptionCacheKey = (type: BioSampleKitType): string => CacheKey.from(['cd', type]);

const query = async (
  query: BioSampleContainerQuery,
  next: Maybe<string>,
  options?: ApiOptions,
): Promise<PagedResult<BioSampleContainer>> => {
  const { paging, containers } = await BioSamplingContainerRestServiceV1.queryContainers(
    toRestOptions(options),
    BioSampleContainerQueryMapper.toQueryV1(query),
    {
      next,
      limit: query.pageSize,
      count: !!query.count,
    },
  );

  return PagedResultMapper.toPagedResult(paging, BioSampleContainerMapper.toContainers(containers));
};

const queryCachedByRefs = async (sampleContainerRefs: BioSampleContainerRef[]): Promise<BioSampleContainer[]> => {
  const refsNeededToBeFetched: BioSampleContainerRef[] = [];
  const containers: BioSampleContainer[] = [];

  sampleContainerRefs.forEach(ref => {
    const cachedContainer = MemCacheService.getValue<BioSampleContainer>(toContainerCacheKey(ref));
    if (cachedContainer) {
      containers.push(cachedContainer);
    } else {
      refsNeededToBeFetched.push(ref);
    }
  });

  if (refsNeededToBeFetched.length) {
    const { data } = await query(
      {
        pageSize: 5_000,
        criteria: {
          sampleContainerRefs: refsNeededToBeFetched,
        },
      },
      undefined,
    );
    data.forEach(fetchedContainer => {
      MemCacheService.setValue(
        toContainerCacheKey({
          kitCode: fetchedContainer.kitCode,
          containerNumber: fetchedContainer.containerNumber,
        }),
        CACHE_TTL_SECS,
        fetchedContainer,
      );
    });
    containers.push(...data);
  }

  return containers;
};

const queryKitDescriptionByTypes = async (
  kitTypes: BioSampleKitType[],
  options?: ApiOptions,
): Promise<KitTypeAndDescriptions> => {
  const uniqueKitTypes = uniq(kitTypes);

  const queryV1: BioSamplingSupplyKitInfoQueryInV1 = BioSampleContainerQueryMapper.toKitInfoQueryV1(uniqueKitTypes);

  const infosV1 = await BioSamplingContainerRestServiceV1.queryKitInfos(toRestOptions(options), queryV1);

  const fetchedTypeAndDescriptions: KitTypeAndDescription[] = uniqueKitTypes.map(kitType => ({
    kitType,
    kitDescription: infosV1.kitInfos.find(kitInfo => kitInfo.kitType === kitType)?.kitDescriptions[0], // First index contains latest updated description
  }));

  return KitTypeAndDescriptionsMapper.from(uniqueKitTypes, fetchedTypeAndDescriptions);
};

const queryCachedKitDescriptionByTypes = async (
  kitTypes: BioSampleKitType[],
  options?: ApiOptions,
): Promise<KitTypeAndDescriptions> => {
  const uniqueKitTypes = uniq(kitTypes);

  const kitTypesNeededToBeFetched: BioSampleKitType[] = [];
  const kitTypeAndDescriptions: KitTypeAndDescription[] = [];

  uniqueKitTypes.forEach(type => {
    const cached = MemCacheService.getValue<KitTypeAndDescription>(toDescriptionCacheKey(type));
    if (cached) {
      kitTypeAndDescriptions.push(cached);
    } else {
      kitTypesNeededToBeFetched.push(type);
    }
  });

  if (kitTypesNeededToBeFetched.length) {
    const typeAndDescriptions = await queryKitDescriptionByTypes(kitTypesNeededToBeFetched, options);
    kitTypeAndDescriptions.push(...typeAndDescriptions.items);
    typeAndDescriptions.items.forEach(typeAndDescription =>
      MemCacheService.setValue(toDescriptionCacheKey(typeAndDescription.kitType), CACHE_TTL_SECS, typeAndDescription),
    );
  }

  return KitTypeAndDescriptionsMapper.from(uniqueKitTypes, kitTypeAndDescriptions);
};

export const BioSampleContainerQueryService = {
  query,
  queryCachedByRefs,
  queryKitDescriptionByTypes,
  queryCachedKitDescriptionByTypes,
};
